import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { deviceService } from "@/services/deviceService";
import type { ChangeDeviceOwnershipInput, Device, UpdateDeviceInput } from "@/types/device";

export interface DeviceHistoryRange {
  startDate?: number;
  endDate?: number;
}

export const deviceKeys = {
  all: ["devices"] as const,
  lists: () => [...deviceKeys.all, "list"] as const,
  list: (filters: unknown) => [...deviceKeys.lists(), { filters }] as const,
  details: () => [...deviceKeys.all, "detail"] as const,
  detail: (id: number | string | undefined) => [...deviceKeys.details(), id] as const,
  history: (id: number | string | undefined) => [...deviceKeys.all, "history", id] as const,
  historyRange: (id: number | string | undefined, range: DeviceHistoryRange | undefined) =>
    [...deviceKeys.history(id), range] as const,
  brands: ["device-brands"] as const,
  brandDevices: (brandId: number | string | undefined) =>
    [...deviceKeys.all, "brand", brandId] as const,
};

export const useDevices = (options: Partial<UseQueryOptions<Device[]>> = {}) => {
  return useQuery({
    queryKey: deviceKeys.lists(),
    queryFn: deviceService.getDevices,
    ...options,
  });
};

export const useDevice = (
  id: number | string | undefined,
  options: Partial<UseQueryOptions<Device>> = {},
) => {
  return useQuery({
    queryKey: deviceKeys.detail(id),
    queryFn: () => deviceService.getDevice(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useDeviceHistory = (
  id: number | string | undefined,
  range?: DeviceHistoryRange,
  options: Record<string, unknown> = {},
) => {
  return useQuery({
    queryKey: deviceKeys.historyRange(id, range),
    queryFn: () =>
      deviceService.getDeviceHistory(id as number | string, range?.startDate, range?.endDate),
    enabled: !!id,
    ...options,
  });
};

export const useDeviceBrands = (options: Record<string, unknown> = {}) => {
  return useQuery({
    queryKey: deviceKeys.brands,
    queryFn: deviceService.getDeviceBrands,
    ...options,
  });
};

export const useDevicesByBrand = (
  brandId: number | string | undefined,
  options: Record<string, unknown> = {},
) => {
  return useQuery({
    queryKey: deviceKeys.brandDevices(brandId),
    queryFn: () => deviceService.getDevicesByBrand(brandId as number | string),
    enabled: !!brandId,
    ...options,
  });
};

export const useCreateDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deviceService.createDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

export const useUpdateDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateDeviceInput }) =>
      deviceService.updateDevice(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(variables.id) });
    },
  });
};

export const useUpdateDeviceName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: number | string; name: string }) =>
      deviceService.updateDeviceName(id, { name }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(variables.id) });
    },
  });
};

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deviceService.deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

export const useDeleteFramesBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deviceId,
      frames,
    }: {
      deviceId: number | string;
      frames: { fcnt: number; time_created: number }[];
    }) => deviceService.deleteFramesBatch(deviceId, frames),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.history(variables.deviceId) });
    },
  });
};

export const useDeleteAllFrames = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId: number | string) => deviceService.deleteAllFrames(deviceId),
    onSuccess: (_data, deviceId) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.history(deviceId) });
    },
  });
};

export const useUpdateDeviceClaimCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deviceId,
      claimCode,
    }: {
      deviceId: number | string;
      claimCode: string;
    }) => deviceService.updateDeviceClaimCode(deviceId, { claim_code: claimCode }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(variables.deviceId) });
    },
  });
};

export const useUpdateDeviceOwnership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deviceId,
      organisationId,
    }: {
      deviceId: number | string;
      organisationId: number | string;
    }) =>
      deviceService.changeDeviceOwnership(deviceId, {
        organisation_id: Number(organisationId),
      } satisfies ChangeDeviceOwnershipInput),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });

      const deviceId = parseInt(String(variables.deviceId));
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(deviceId) });

      queryClient.invalidateQueries({ queryKey: ["devices", "detail"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["organisations"], exact: false });
    },
  });
};
